import { z } from 'zod';

/**
 * Policy dei file ammessi: UNICO punto da toccare per
 * aggiungere un'estensione o cambiare un limite. Consumata sia dall'API
 * (validazione upload) sia dal web (attributo `accept`, messaggi d'avviso).
 */

export const CategoriaFile = z.enum(['documento', 'foto']);
export type CategoriaFile = z.infer<typeof CategoriaFile>;

export interface RegolaFormatoFile {
  estensione: string;
  mimeAmmessi: string[];
  /**
   * true se il formato ha una firma binaria che una libreria di sniffing
   * (es. `file-type`) può confermare; false se si accetta sulla sola
   * estensione/`content-type` dichiarato — nessuna firma affidabile
   * (testo puro) o non coperta dalla libreria (es. CAD).
   */
  verificaFirma: boolean;
}

export interface PolicyCategoriaFile {
  formati: RegolaFormatoFile[];
  dimensioneMassimaMb: number;
  /** Soglia di avviso non bloccante lato client, prima di iniziare il transfer. */
  sogliaAvvisoMb?: number;
}

export const POLICY_FILE: Record<CategoriaFile, PolicyCategoriaFile> = {
  documento: {
    dimensioneMassimaMb: 60,
    sogliaAvvisoMb: 15,
    formati: [
      { estensione: 'pdf', mimeAmmessi: ['application/pdf'], verificaFirma: true },
      { estensione: 'doc', mimeAmmessi: ['application/msword'], verificaFirma: true },
      {
        estensione: 'docx',
        mimeAmmessi: ['application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        verificaFirma: true,
      },
      { estensione: 'xls', mimeAmmessi: ['application/vnd.ms-excel'], verificaFirma: true },
      {
        estensione: 'xlsx',
        mimeAmmessi: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
        verificaFirma: true,
      },
      { estensione: 'txt', mimeAmmessi: ['text/plain'], verificaFirma: false },
      // CAD: DWG ha una firma reale ("AC10..."), ma non coperta dalla libreria
      // di sniffing usata per gli altri formati — richiederebbe un controllo
      // dedicato. DXF è testuale, nessuna firma possibile. Entrambi accettati
      // per ora sulla sola estensione.
      {
        estensione: 'dwg',
        mimeAmmessi: ['image/vnd.dwg', 'application/acad'],
        verificaFirma: false,
      },
      {
        estensione: 'dxf',
        mimeAmmessi: ['image/vnd.dxf', 'application/dxf'],
        verificaFirma: false,
      },
    ],
  },
  foto: {
    dimensioneMassimaMb: 15,
    formati: [
      { estensione: 'jpg', mimeAmmessi: ['image/jpeg'], verificaFirma: true },
      { estensione: 'jpeg', mimeAmmessi: ['image/jpeg'], verificaFirma: true },
      { estensione: 'png', mimeAmmessi: ['image/png'], verificaFirma: true },
      // Da verificare in implementazione: sharp/libvips non garantisce il
      // supporto nativo del BMP in lettura.
      { estensione: 'bmp', mimeAmmessi: ['image/bmp'], verificaFirma: true },
      { estensione: 'webp', mimeAmmessi: ['image/webp'], verificaFirma: true },
      { estensione: 'heic', mimeAmmessi: ['image/heic'], verificaFirma: true },
      { estensione: 'heif', mimeAmmessi: ['image/heif'], verificaFirma: true },
    ],
  },
};

/**
 * Ogni foto accettata viene sempre normalizzata a questo formato,
 * indipendentemente da come è stata caricata: mai un pass-through.
 */
export const NORMALIZZAZIONE_FOTO = {
  latoLungoMassimoPx: 3000,
  formatoOutput: 'image/jpeg',
} as const;
